import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertCircle, LayoutDashboard } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Skeleton } from '@/components/ui/misc';
import { useUserOptions } from '@/hooks/use-user-options';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboard, useDashboards, useMyDashboard, type Widget } from './api';

/** Enough distinct hues for a pie without any two reading as the same. */
const PIE_COLOURS = ['#dc2626', '#0891b2', '#16a34a', '#ca8a04', '#7c3aed', '#db2777', '#0f766e'];

/**
 * The role dashboard (spec RPT-01, RPT-02).
 *
 * The filters at the top apply to every widget at once, which is the whole
 * point of RPT-02: a director narrowing to one owner should not have to
 * narrow nine tiles one at a time.
 */
export function InsightsDashboardPage() {
  const [dashboardId, setDashboardId] = useState<string>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [ownerId, setOwnerId] = useState('');

  const filters = {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(ownerId ? { ownerId } : {}),
  };

  const { data: dashboards } = useDashboards();
  const { data: users } = useUserOptions();
  const mine = useMyDashboard(filters);
  const picked = useDashboard(dashboardId || undefined, filters);

  const dashboard = dashboardId ? picked.data : mine.data;
  const isLoading = dashboardId ? picked.isLoading : mine.isLoading;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Your role's view of the business. Every tile is a live query, and every one respects what you are allowed to see."
        actions={
          <Select
            value={dashboardId}
            onChange={(event) => setDashboardId(event.target.value)}
            className="w-44"
          >
            <option value="">My dashboard</option>
            {(dashboards ?? []).map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
        }
      />

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <Label htmlFor="filter-from" className="text-xs">
              From
            </Label>
            <Input
              id="filter-from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="h-9 w-40"
            />
          </div>
          <div>
            <Label htmlFor="filter-to" className="text-xs">
              To
            </Label>
            <Input
              id="filter-to"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="h-9 w-40"
            />
          </div>
          <div>
            <Label htmlFor="filter-owner" className="text-xs">
              Owner
            </Label>
            <Select
              id="filter-owner"
              value={ownerId}
              onChange={(event) => setOwnerId(event.target.value)}
              className="h-9 w-48"
            >
              <option value="">Everyone</option>
              {(users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </div>
          {(from || to || ownerId) && (
            <button
              onClick={() => {
                setFrom('');
                setTo('');
                setOwnerId('');
              }}
              className="pb-2 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              Clear
            </button>
          )}
          <p className="ml-auto pb-2 text-xs text-muted-foreground">
            Filters apply to every tile at once.
          </p>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-28" />
          ))}
        </div>
      ) : !dashboard ? (
        <Card>
          <CardContent className="py-14 text-center">
            <LayoutDashboard className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
            <p className="font-medium">No dashboard yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              One is created for each role the first time somebody opens this page.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
          {dashboard.widgets.map((widget) => (
            <WidgetTile key={widget.id} widget={widget} />
          ))}
        </div>
      )}
    </div>
  );
}

function WidgetTile({ widget }: { widget: Widget }) {
  // The API's 12-column grid mapped onto Tailwind's, and left full width on a
  // phone where a three-column tile is unreadable.
  const span =
    widget.width >= 12
      ? 'lg:col-span-12'
      : widget.width >= 8
        ? 'lg:col-span-8'
        : widget.width >= 6
          ? 'lg:col-span-6'
          : widget.width >= 4
            ? 'lg:col-span-4'
            : 'lg:col-span-3';

  return (
    <Card className={cn('col-span-1 sm:col-span-1', span)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{widget.title}</CardTitle>
      </CardHeader>
      <CardContent>
        {widget.error ? (
          <p className="flex items-start gap-2 py-3 text-sm text-muted-foreground">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {widget.error}
          </p>
        ) : (
          <WidgetBody widget={widget} />
        )}
      </CardContent>
    </Card>
  );
}

function WidgetBody({ widget }: { widget: Widget }) {
  const data = widget.data;
  if (!data) return <p className="py-3 text-sm text-muted-foreground">No data</p>;

  switch (data.kind) {
    case 'money':
      return (
        <div>
          <p className="tabular text-2xl font-semibold">{formatMoney(data.value, data.currency)}</p>
          {data.hint && <p className="mt-1 text-xs text-muted-foreground">{data.hint}</p>}
        </div>
      );

    case 'percent':
      return (
        <div>
          <p className="tabular text-2xl font-semibold">{data.value}%</p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${Math.min(100, Math.max(0, data.value))}%` }}
            />
          </div>
          {data.hint && <p className="mt-1 text-xs text-muted-foreground">{data.hint}</p>}
        </div>
      );

    case 'number':
      return (
        <div>
          <p className="tabular text-2xl font-semibold">
            {data.value}
            {data.unit && <span className="ml-1 text-sm font-normal text-muted-foreground">{data.unit}</span>}
          </p>
          {data.hint && <p className="mt-1 text-xs text-muted-foreground">{data.hint}</p>}
        </div>
      );

    case 'series': {
      if (data.points.length === 0) {
        return <p className="py-8 text-center text-sm text-muted-foreground">Nothing yet</p>;
      }
      const money = data.unit === 'money';
      const format = (value: number) => (money ? formatMoney(String(value)) : String(value));

      if (widget.chartType === 'pie' || widget.chartType === 'donut') {
        return (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.points}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={widget.chartType === 'donut' ? 46 : 0}
                  outerRadius={78}
                  label={(entry: { label: string }) => entry.label}
                  labelLine={false}
                >
                  {data.points.map((_point, index) => (
                    <Cell key={index} fill={PIE_COLOURS[index % PIE_COLOURS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => format(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );
      }

      if (widget.chartType === 'line') {
        return (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.points}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={56} />
                <Tooltip formatter={(value: number) => format(value)} />
                <Line type="monotone" dataKey="value" stroke="#dc2626" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      }

      return (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.points}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={56} />
              <Tooltip formatter={(value: number) => format(value)} />
              <Bar dataKey="value" fill="#dc2626" maxBarSize={40} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }

    case 'list':
      return (
        <div className="space-y-1.5">
          {data.rows.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">Nothing due</p>
          )}
          {data.rows.map((row, index) => (
            <div key={index} className="flex items-baseline gap-2 border-b py-1.5 last:border-0">
              <span className="min-w-0 flex-1 truncate text-sm">
                {String(row.title ?? row.name ?? '—')}
              </span>
              {row.company || row.project ? (
                <span className="truncate text-xs text-muted-foreground">
                  {String(row.company ?? row.project)}
                </span>
              ) : null}
              {row.value ? (
                <span className="tabular text-sm">{formatMoney(String(row.value))}</span>
              ) : null}
              {row.date ? (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatDate(String(row.date))}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
}
